// Flow item (a.k.a. material item / "изделие") detail + edit page.
// Reachable from /flows list, /traceability, or directly.
//
// Read-only sidebar shows immutable fields (id, created/updated dates,
// parent ids). Mutable form covers code, name, status, stage, qty/unit,
// metadata JSON. RFID tag is managed via the universal TagsSection.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { TagsSection } from "@/components/TagsSection";
import { ErrorState, LoadingState } from "@/components/States";
import { flowsApi, stagesApi } from "@/lib/api";
import { displayName, formatDateTime } from "@/lib/format";
import {
  FlowStatus,
  FlowType,
  type FlowStatus as FlowStatusType,
  type FlowType as FlowTypeType,
  type MaterialItemRead,
} from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

export default function FlowDetail() {
  const { id } = useParams<{ id: string }>();

  const q = useQuery({
    queryKey: ["flow", id],
    queryFn: () => flowsApi.get(id!),
    enabled: !!id,
  });

  if (!id) return <ErrorState error={new Error("Не указан идентификатор изделия.")} />;
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  return <Inner item={q.data!} />;
}

function Inner({ item }: { item: MaterialItemRead }) {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader
        eyebrow={`flow · ${item.flow_type.toLowerCase()}`}
        title={item.code}
        description={item.name_ru ?? t("flowDetail.noNameHint")}
        actions={
          <div className="flex items-center gap-2">
            <Link to={`/traceability?id=${item.id}`} className="btn" title={t("flowDetail.traceabilityTitle")}>
              {t("flowDetail.traceability")}
            </Link>
            <Link to={`/flows/${item.id}/passport`} className="btn">
              {t("flowDetail.passport")}
            </Link>
            <Link to="/flows" className="btn">
              {t("flowDetail.allItems")}
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <IdentitySection item={item} />
        <SummarySection item={item} />
        <TagsSection
          targetType="flow_item"
          targetId={item.id}
          hint={t("flowDetail.tagsHint")}
        />
      </div>
    </>
  );
}

function IdentitySection({ item }: { item: MaterialItemRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState(item.code);
  const [nameRu, setNameRu] = useState(item.name_ru ?? "");
  const [flowType, setFlowType] = useState<FlowTypeType>(item.flow_type);
  const [stageId, setStageId] = useState<number | null>(item.stage_id);
  const [status, setStatus] = useState<FlowStatusType>(item.status);
  const [quantity, setQuantity] = useState(item.quantity?.toString() ?? "");
  const [unit, setUnit] = useState(item.unit ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setCode(item.code);
    setNameRu(item.name_ru ?? "");
    setFlowType(item.flow_type);
    setStageId(item.stage_id);
    setStatus(item.status);
    setQuantity(item.quantity?.toString() ?? "");
    setUnit(item.unit ?? "");
  }, [item.id, item.code, item.name_ru, item.flow_type, item.stage_id, item.status, item.quantity, item.unit]);

  const stages = useQuery({
    queryKey: ["stages", "all"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
    staleTime: 60_000,
  });

  const m = useMutation({
    mutationFn: () =>
      flowsApi.update(item.id, {
        code: code.trim(),
        name_ru: nameRu.trim() || null,
        flow_type: flowType,
        stage_id: stageId,
        status,
        quantity: quantity.trim() ? Number(quantity) : null,
        unit: unit.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flow", item.id] });
      qc.invalidateQueries({ queryKey: ["flows", "list"] });
      setSavedAt(Date.now());
      setErr(null);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  return (
    <section className="card p-5 space-y-4">
      <h3 className="text-lg">{t("flowDetail.identity")}</h3>

      <Field label={t("flowDetail.codeLabel")}>
        <input
          className={`${INPUT_CLS} font-mono`}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
      </Field>
      <Field label={t("flowDetail.nameLabel")}>
        <input
          className={INPUT_CLS}
          value={nameRu}
          onChange={(e) => setNameRu(e.target.value)}
          placeholder={t("flowDetail.namePlaceholder")}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("flowDetail.flowTypeLabel")}>
          <select
            className={`${INPUT_CLS} font-mono text-sm`}
            value={flowType}
            onChange={(e) => setFlowType(e.target.value as FlowTypeType)}
          >
            {FlowType.map((ft) => (
              <option key={ft} value={ft}>
                {ft}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("flowDetail.statusLabel")}>
          <select
            className={`${INPUT_CLS} font-mono text-sm`}
            value={status}
            onChange={(e) => setStatus(e.target.value as FlowStatusType)}
          >
            {FlowStatus.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={t("flowDetail.stageLabel")}>
        <select
          className={`${INPUT_CLS} font-mono text-sm`}
          value={stageId ?? ""}
          onChange={(e) => setStageId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{t("flowDetail.stageNotSet")}</option>
          {stages.data?.items.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} · {displayName(s)}
            </option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("flowDetail.quantityLabel")}>
          <input
            className={`${INPUT_CLS} tabular-nums`}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            type="number"
            step="any"
            min="0"
          />
        </Field>
        <Field label={t("flowDetail.unitLabel")}>
          <input
            className={INPUT_CLS}
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="шт / кг / м²"
          />
        </Field>
      </div>

      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary"
          onClick={() => {
            setErr(null);
            m.mutate();
          }}
          disabled={m.isPending || !code.trim()}
        >
          {m.isPending ? t("common.saving") : t("common.save")}
        </button>
        {savedAt && !m.isPending && (
          <span className="text-text-muted text-xs">{t("flowDetail.savedAt", { time: formatDateTime(new Date(savedAt).toISOString()) })}</span>
        )}
      </div>
    </section>
  );
}

function SummarySection({ item }: { item: MaterialItemRead }) {
  const { t } = useTranslation();
  return (
    <section className="card p-5 space-y-3">
      <h3 className="text-lg flex items-center gap-3">
        {t("flowDetail.summary")}
        <StatusPill kind="flow" value={item.status} />
      </h3>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Kv k="ID" v={<span className="font-mono text-xs">{item.id}</span>} />
        <Kv k={t("flowDetail.rfidTag")} v={item.rfid_tag ? <span className="font-mono">{item.rfid_tag}</span> : "—"} />
        <Kv
          k={t("flowDetail.batch")}
          v={item.batch_id ? <span className="font-mono text-xs">{item.batch_id}</span> : "—"}
        />
        <Kv
          k={t("flowDetail.source")}
          v={item.source ? <span className="font-mono text-xs">{item.source}</span> : "—"}
        />
        <Kv k={t("common.created")} v={formatDateTime(item.created_at)} />
        <Kv k={t("common.updated")} v={formatDateTime(item.updated_at)} />
      </dl>
      {item.parent_ids && item.parent_ids.length > 0 && (
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
            {t("flowDetail.parentItems")}
          </p>
          <ul className="space-y-1">
            {item.parent_ids.map((pid) => (
              <li key={pid}>
                <Link to={`/flows/${pid}`} className="font-mono text-xs text-accent-strong hover:underline">
                  {pid}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {item.measured_metrics && Object.keys(item.measured_metrics).length > 0 && (
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
            {t("flowDetail.measuredMetrics")}
          </p>
          <pre className="text-xs bg-surface-alt rounded-md p-3 overflow-x-auto">
            {JSON.stringify(item.measured_metrics, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Kv({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <dt className="text-text-muted text-xs uppercase tracking-wider font-mono">{k}</dt>
      <dd className="mt-0.5 text-text-primary">{v}</dd>
    </div>
  );
}
